import React, { useCallback } from 'react';
import { TopPerformersTable } from './TopPerformersTable';
import { useTopPerformers } from './useTopPerformers';
import {
  EDashboardBookingStatus,
  ESalesMetricsGroupBy,
  PerformerCategory,
  TopPerformersSortBy,
  TopPerformersSortDirection,
} from 'services/DashboardApi/types';
import { LoadingBar, ErrorBar } from 'ui/NetworkStatusBar';

interface ITopPerformersProps {
  startYear: number;
  startMonth: number;
  endYear: number;
  endMonth: number;
  dateType: ESalesMetricsGroupBy;
  status?: EDashboardBookingStatus[];
  companyCountry?: string[];
  company?: string[];
  destination?: string[];
  hotel?: string[];
  salesUserUuid?: string;
}

export const TopPerformers: React.FC<ITopPerformersProps> = ({
  startYear,
  startMonth,
  endYear,
  endMonth,
  dateType,
  status,
  companyCountry,
  company,
  destination,
  hotel,
  salesUserUuid,
}) => {
  const { data, tableConfigs, isLoading, isError, updateCategory, isUpdatingCategory } = useTopPerformers({
    startYear,
    startMonth,
    endYear,
    endMonth,
    dateType,
    status,
    companyCountry,
    company,
    destination,
    hotel,
    salesUserUuid,
  });

  const handleSortChange = useCallback(
    (category: PerformerCategory, sortBy: TopPerformersSortBy, sortDirection: TopPerformersSortDirection) => {
      updateCategory(category, { sortBy, sortDirection });
    },
    [updateCategory]
  );

  const handleToggleExpand = useCallback(
    (category: PerformerCategory) => {
      const currentLimit = tableConfigs[category].limit;
      const newLimit = currentLimit === 5 ? 10 : 5;
      updateCategory(category, { limit: newLimit });
    },
    [tableConfigs, updateCategory]
  );

  if (isLoading) {
    return (
      <div className="w-full flex justify-center items-center py-[40px]">
        <LoadingBar label="Loading Top Performers" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="w-full flex justify-center py-[40px]">
        <ErrorBar message="Failed to load top performers content" />
      </div>
    );
  }

  return (
    <div className="top-performers mt-[40px]">
      <div className="grid grid-cols-1 crm-desktop:grid-cols-3 gap-[20px] crm-desktop:gap-[30px]">
        <TopPerformersTable
          title="Top 5 Travel Companies"
          data={data.topTCs}
          totalCount={data.totalTCs}
          config={tableConfigs.TC}
          onSortChange={(sortBy, sortDirection) => handleSortChange('TC', sortBy, sortDirection)}
          onToggleExpand={() => handleToggleExpand('TC')}
          isUpdating={isUpdatingCategory === 'TC'}
        />
        <TopPerformersTable
          title="Top 5 Travel Agents"
          data={data.topTAs}
          totalCount={data.totalTAs}
          config={tableConfigs.TA}
          onSortChange={(sortBy, sortDirection) => handleSortChange('TA', sortBy, sortDirection)}
          onToggleExpand={() => handleToggleExpand('TA')}
          isUpdating={isUpdatingCategory === 'TA'}
        />
        <TopPerformersTable
          title="Top 5 Hotels"
          data={data.topHotels}
          totalCount={data.totalHotels}
          config={tableConfigs.HOTEL}
          onSortChange={(sortBy, sortDirection) => handleSortChange('HOTEL', sortBy, sortDirection)}
          onToggleExpand={() => handleToggleExpand('HOTEL')}
          isUpdating={isUpdatingCategory === 'HOTEL'}
        />
      </div>
    </div>
  );
};
